import React from 'react';
import { Heart, Star, Gift, HandCoins, Zap, Loader2 } from 'lucide-react';
import { useLatestStats } from '../hooks/useLatestStats';

const CURRENCY_SYMBOL = {
  EUR: '\u20AC',
  USD: '$',
  GBP: '\u00A3'
};

function StatBox({ icon: Icon, label, name, sub, color, dataTestId }) {
  const colorMap = {
    pink: 'from-pink-500/20 to-fuchsia-600/10 border-pink-500/30 text-pink-300',
    violet:
      'from-violet-500/20 to-indigo-600/10 border-violet-500/30 text-violet-300',
    amber:
      'from-amber-500/20 to-orange-600/10 border-amber-500/30 text-amber-300',
    emerald:
      'from-emerald-500/20 to-teal-600/10 border-emerald-500/30 text-emerald-300',
    cyan:
      'from-cyan-500/20 to-sky-600/10 border-cyan-500/30 text-cyan-300'
  };
  return (
    <div
      className="rounded-xl border border-white/10 bg-white/[0.03] p-3 hover:border-violet-500/30 transition-colors"
      data-testid={dataTestId}
    >
      <div className="flex items-center gap-2 mb-1.5">
        <span
          className={`w-6 h-6 shrink-0 rounded-md bg-gradient-to-br border ${colorMap[color]} flex items-center justify-center`}
        >
          <Icon className="w-3 h-3" />
        </span>
        <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold truncate">
          {label}
        </div>
      </div>
      <div className="text-white font-bold break-words text-base">
        {name || '\u2014'}
      </div>
      {sub && (
        <div className="text-xs text-zinc-400 line-clamp-2 mt-0.5">{sub}</div>
      )}
    </div>
  );
}

function tierLabel(tier) {
  if (!tier) return 'Sub';
  if (tier === '1000') return 'Tier 1';
  if (tier === '2000') return 'Tier 2';
  if (tier === '3000') return 'Tier 3';
  return 'Sub';
}

function formatDonation(d) {
  if (!d) return null;
  const sym = CURRENCY_SYMBOL[d.currency] || (d.currency ? d.currency + ' ' : '');
  const amount = d.amount != null ? `${sym}${d.amount}` : '';
  return amount;
}

export default function LatestStatsGrid() {
  const { data, loading } = useLatestStats();

  if (loading && !data) {
    return (
      <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 w-full">
        {[0, 1, 2, 3, 4].map((i) => (
          <div
            key={i}
            className="h-20 rounded-xl border border-white/10 bg-white/[0.02] flex items-center justify-center"
          >
            <Loader2 className="w-4 h-4 text-zinc-600 animate-spin" />
          </div>
        ))}
      </div>
    );
  }

  const follower = data?.follower;
  const subscriber = data?.subscriber;
  const giftSub = data?.gift_sub;
  const cheer = data?.cheer;
  const donation = data?.donation;

  return (
    <div
      className="mt-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 w-full"
      data-testid="latest-stats-grid"
    >
      <StatBox
        icon={Heart}
        label="Volger"
        color="pink"
        name={follower?.name}
        sub={follower ? 'Nieuwste volger' : 'Nog niet beschikbaar'}
        dataTestId="stat-latest-follower"
      />
      <StatBox
        icon={Star}
        label="Sub"
        color="violet"
        name={subscriber?.name}
        sub={
          subscriber
            ? `${tierLabel(subscriber.tier)}${
                subscriber.gifter ? ` \u2022 cadeau van ${subscriber.gifter}` : ''
              }`
            : 'Nog niet beschikbaar'
        }
        dataTestId="stat-latest-sub"
      />
      <StatBox
        icon={Gift}
        label="Gift sub"
        color="amber"
        name={giftSub?.sender}
        sub={
          giftSub
            ? `Cadeau aan ${giftSub.recipient || '?'}${
                giftSub.tier ? ` \u2022 ${tierLabel(giftSub.tier)}` : ''
              }`
            : 'Nog niet beschikbaar'
        }
        dataTestId="stat-latest-gift-sub"
      />
      <StatBox
        icon={Zap}
        label="Bits"
        color="cyan"
        name={cheer?.name}
        sub={
          cheer
            ? `${cheer.amount?.toLocaleString('nl-NL')} bits${
                cheer.message ? ` \u2022 \u201C${cheer.message}\u201D` : ''
              }`
            : 'Nog niet beschikbaar'
        }
        dataTestId="stat-latest-bits"
      />
      <StatBox
        icon={HandCoins}
        label="Donatie"
        color="emerald"
        name={donation?.username}
        sub={
          donation
            ? `${formatDonation(donation)}${
                donation.message ? ` \u2022 \u201C${donation.message}\u201D` : ''
              }`
            : 'Nog niet beschikbaar'
        }
        dataTestId="stat-latest-donation"
      />
    </div>
  );
}
